import { GetServerSidePropsContext, NextPage } from 'next'
import { QueryClient, dehydrate } from '@tanstack/query-core'
import { buildRedirectServerSideProps } from '../../utils/Helper'
import { createAxiosInstance } from '../../contexts/axios/AxiosContext'
import { environmentConfig } from '../../config/environment/environmentConfig'
import { getPropertyMicroSite } from '../../services/property-microsite/PropertyMicrositeService'
import { useRouter } from 'next/router'
import ComponentManager from '../../components/component-manager/ComponentManager'
import Layout from '../../layout/Layout'
import React, { useMemo } from 'react'
import useMicrositePageMetadata from '../../hooks/useMicrositePageMetadata'
import usePropertyMicroSite, {
    PROPERTY_MICRO_SITE_QUERY_KEY
} from '../../hooks/api/property-microsite/usePropertyMicrosite'
import useSendFormsBeforeLeavePage from '../../hooks/useSendFormsBeforeLeavePage'

const PropertyPage: NextPage<{ themeValue: string }> = ({ themeValue }) => {
    useSendFormsBeforeLeavePage('property')
    const {
        query: { id: slug }
    } = useRouter()
    const { data, isLoading } = usePropertyMicroSite(slug.toString())
    const { seo, contact, header } = useMicrositePageMetadata({ data, slug: slug as string, isForProperty: true })

    const status = useMemo(() => {
        switch (data?.state) {
            case 0:
                return {
                    status: 'Dostupné',
                    statusColor: 'success-400'
                }
            case 1:
                return {
                    status: 'Rezervováno',
                    statusColor: 'warning-400'
                }
            case 2:
                return {
                    status: 'Prodáno',
                    statusColor: 'error-400'
                }
            default:
                return {
                    status: 'Nedostupné',
                    statusColor: 'grey-400'
                }
        }
    }, [data?.state])

    if (isLoading) {
        return null
    }

    return (
        <Layout
            gradient
            title={data.name}
            externalScripts={data.trackingCodes}
            contact={contact}
            header={header}
            previewVideoUrl={data.previewVideoUrl}
            seo={seo}
            footer={data.footer}
            components={data?.content}
            themeValue={themeValue}
            {...status}
        >
            <ComponentManager type='property' components={data?.content} brokerEmail={contact.email} />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideAndEnvironmentProps = async ({
    params: { id: propertySlug }
}: GetServerSidePropsContext) => {
    const queryClient = new QueryClient()
    const axiosInstance = createAxiosInstance(environmentConfig.apiUrl)
    try {
        const propertyMicroSiteData = await getPropertyMicroSite(axiosInstance, propertySlug.toString())
        // INFO:
        // Set property MicroSite page to react query (SSR)
        const themeValue = propertyMicroSiteData.template.colorTemplate
        await queryClient.prefetchQuery([PROPERTY_MICRO_SITE_QUERY_KEY, propertySlug], () => {
            return propertyMicroSiteData
        })
        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                environmentConfig,
                themeValue
            }
        }
    } catch (error) {
        const redirect = buildRedirectServerSideProps(error)
        if (redirect) {
            return redirect
        }
        return {
            notFound: true
        }
    }
}

export default PropertyPage
